/**
 * Dashboard — landing page for authenticated users.
 *
 * Layout (desktop-first, 1280–1920 primary):
 *   1. Page title + scoped filters (year, portfolio)
 *   2. Hero summary strip (4 tiles, tabular-nums, semantic accents)
 *   3. "Filings needing your action" primary card (list, sorted by deadline)
 *   4. "Recently completed" collapsed card with deep link
 *
 * State contract:
 *   - Initial load → <DashboardSkeleton /> (layout-stable, no text-only loader)
 *   - Scoped error → inline retry via TanStack refetch (no window.reload)
 *   - Empty (no filings, no buildings) → <EmptyState /> with PM / Add CTAs
 *   - Role fork → owners see owner-held filings; staff/RDP see rdp-held
 *     (ownership from statusTaxonomy.deriveOwnership). Richer RDP workflows
 *     still live on /rdp/* — this is the "quick glance" for any authed user.
 *
 * Notes:
 *   - Year filter retains the 2024/2025 choices from the prior UI; adding
 *     2026 is a backend concern, not a UI one.
 *   - We keep the NewBuildingDialog as-is and route both empty-state CTAs
 *     to it; the "Connect Portfolio Manager" button sets an initial tab
 *     through a local state flag we pass down. If the dialog later learns
 *     a `defaultTab` prop, swap out the workaround.
 */

import React, { useMemo, useState } from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";

import {
  useCompanyQuery,
  useCompanyFilingsQuery,
  usePortfoliosQuery,
  useMeQuery,
} from "@/queries/queries";

import { Button } from "@/components/shared/ui/button";
import { Card } from "@/components/shared/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/shared/ui/select";

import NewBuildingDialog from "@/pages/Dashboard/NewBuildingDialog";

import { HeroStrip, type CategoryCounts } from "./HeroStrip";
import { NeedsActionCard } from "./NeedsActionCard";
import { CompletedCard } from "./CompletedCard";
import { DashboardSkeleton } from "./DashboardSkeleton";
import { EmptyState } from "./EmptyState";
import {
  selectActionFilings,
  selectCompletedCount,
  useDashboardMetrics,
  type ViewerRole,
} from "./useDashboardMetrics";
import type { FilingCategory, FilingsData } from "@/api/user";

/**
 * Count filings inside a FilingCategory. Prefer `.count` from the serializer;
 * fall back to summing items length for older payload shapes.
 */
const categoryCount = (cat: FilingCategory | undefined): number => {
  if (!cat) return 0;
  if (typeof cat.count === "number") return cat.count;
  const ll97 = cat.items?.lL97?.length ?? 0;
  const ll88 = cat.items?.lL88?.length ?? 0;
  return ll97 + ll88;
};

const deriveCategoryCounts = (
  data: FilingsData | undefined,
): CategoryCounts => ({
  notStarted: categoryCount(data?.unstarted),
  inProgress: categoryCount(data?.inProgress),
  inReview: categoryCount(data?.inReview),
  submitted: categoryCount(data?.submitted),
});

function resolveViewerRole(user: unknown): ViewerRole {
  const u = user as
    | { isStaff?: boolean; rdpInfo?: { id?: string } | null }
    | null
    | undefined;
  if (u?.isStaff) return "staff";
  if (u?.rdpInfo?.id) return "rdp";
  return "owner";
}

function viewerRoleLabel(role: ViewerRole): string {
  if (role === "owner") return "assigned to you";
  if (role === "rdp") return "awaiting RDP review";
  return "staff queue";
}

const Dashboard = () => {
  const [dialogIsOpen, setDialogIsOpen] = useState(false);
  const [selectedYear, setSelectedYear] = useState<number>(2025);
  const [selectedPortfolio, setSelectedPortfolio] =
    useState<string>("All Portfolios");
  const [selectedArticle, setSelectedArticle] = useState<string>("All Articles");

  const { data: user } = useMeQuery();
  const viewerRole = resolveViewerRole(user);

  const { data: companyData, isLoading: companyLoading } = useCompanyQuery(
    user?.company?.id || "",
  );

  const {
    data: filingsData,
    isLoading: filingsLoading,
    isError,
    refetch,
    isFetching,
  } = useCompanyFilingsQuery(
    companyData?.id || "",
    selectedYear,
    selectedPortfolio === "All Portfolios" ? null : selectedPortfolio,
    selectedArticle === "All Articles" ? null : selectedArticle,
  );

  const { data: portfoliosData } = usePortfoliosQuery();

  const metrics = useDashboardMetrics(filingsData, viewerRole);
  const actionItems = useMemo(
    () => selectActionFilings(filingsData, viewerRole),
    [filingsData, viewerRole],
  );
  const completedCount = useMemo(
    () => selectCompletedCount(filingsData),
    [filingsData],
  );
  const categoryCounts = useMemo(
    () => deriveCategoryCounts(filingsData),
    [filingsData],
  );

  const isInitialLoading = !filingsData && (companyLoading || filingsLoading);
  const isEmpty =
    !!filingsData &&
    (filingsData.totalCount === 0 || metrics.totalBuildings === 0);

  return (
    <div className="min-h-full bg-muted/30 px-10 py-8">
      <div className="mx-auto flex w-full max-w-[1920px] flex-col gap-6">
        {/* Title + filter row */}
        <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div className="flex flex-col gap-1">
            <h1 className="text-2xl font-semibold tracking-tight text-foreground">
              Dashboard
            </h1>
            <p className="text-sm text-muted-foreground">
              Your open filings and compliance snapshot.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Select
              value={selectedPortfolio}
              onValueChange={setSelectedPortfolio}
            >
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Portfolio" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="All Portfolios">All portfolios</SelectItem>
                {portfoliosData?.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={String(selectedYear)}
              onValueChange={(v) => setSelectedYear(parseInt(v, 10))}
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Calendar year" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="2025">CY 2025</SelectItem>
                <SelectItem value="2024">CY 2024</SelectItem>
              </SelectContent>
            </Select>
            <Select
              value={selectedArticle}
              onValueChange={setSelectedArticle}
            >
              <SelectTrigger
                className="w-[180px]"
                aria-label="Filter by LL97 article"
              >
                <SelectValue placeholder="LL97 article" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="All Articles">All articles</SelectItem>
                <SelectItem value="320">Article 320</SelectItem>
                <SelectItem value="321">Article 321</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Body */}
        {isInitialLoading && <DashboardSkeleton />}

        {isError && !isInitialLoading && (
          <Card
            density="default"
            role="alert"
            aria-live="assertive"
            className="gap-3 border-danger/40 bg-danger/5"
          >
            <div className="flex items-start gap-3">
              <AlertTriangle
                className="mt-0.5 h-5 w-5 shrink-0 [color:hsl(var(--danger))]"
                aria-hidden="true"
              />
              <div className="flex-1">
                <p className="text-sm font-semibold text-foreground">
                  We couldn&rsquo;t load your dashboard.
                </p>
                <p className="mt-1 text-sm text-muted-foreground">
                  This is usually a transient network issue. Your data is safe.
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => refetch()}
                disabled={isFetching}
              >
                <RefreshCw
                  className={`mr-2 h-4 w-4 ${isFetching ? "animate-spin" : ""}`}
                  aria-hidden="true"
                />
                Retry
              </Button>
            </div>
          </Card>
        )}

        {!isInitialLoading && !isError && isEmpty && (
          <EmptyState
            onAddBuilding={() => setDialogIsOpen(true)}
            onConnectEspm={() => setDialogIsOpen(true)}
          />
        )}

        {!isInitialLoading && !isError && !isEmpty && filingsData && (
          <>
            <HeroStrip
              counts={categoryCounts}
              actionableForYouCount={metrics.awaitingYouCount}
            />

            <NeedsActionCard
              items={actionItems}
              viewerRoleLabel={viewerRoleLabel(viewerRole)}
            />

            <CompletedCard count={completedCount} calendarYear={selectedYear} />

            {/*
              TODO(role-fork): the richer "RDP queue" and "Staff review" workflows
              live on /rdp/* and /staff/*. This dashboard gives them the same
              glanceable hero as owners, but doesn't yet surface queue-specific
              metrics (e.g., assigned-vs-unassigned). Revisit once the queue
              redesign lands.
            */}
          </>
        )}
      </div>

      <NewBuildingDialog isOpen={dialogIsOpen} onOpenChange={setDialogIsOpen} />
    </div>
  );
};

export default Dashboard;
